/**
 * **Which answer moved most** since the previous check-in — the one the task card charts under the
 * answers (owner, 24 September 2026).
 *
 * "Most" is RELATIVE to the question's own scale: a rating moving 4 → 2 is two fifths of its
 * range, a weight moving 68,9 → 68,4 kg is under one percent of the reading — comparing the raw
 * differences would chart the weight every time a client weighs in. A rating is judged against its
 * five steps (`SCALE_STEPS`), a number against the previous reading itself.
 *
 * Only a question that is ANSWERED in the shown occurrence and has a previous answer to move from
 * is a candidate, and only one that has a chartable series; a tie keeps the form's own order.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import {
  SCALE_STEPS,
  type SurveyDeltaTone,
  type SurveyHistoryView,
  type SurveyPlotView,
  type SurveyRowView,
} from './surveyHistory';

export interface BiggestChange {
  readonly row: SurveyRowView;
  readonly series: SurveyPlotView;
  /** The signed change against the previous answered occurrence. */
  readonly delta: number;
  readonly previous: number;
  readonly current: number;
  /** The verdict the change wears — the cell's own, so a measured quantity stays unjudged. */
  readonly tone: SurveyDeltaTone;
}

export function biggestChange(view: SurveyHistoryView, columnId: number): BiggestChange | null {
  const index = view.columns.findIndex((column) => column.id === columnId);
  if (index < 0) return null;

  let best: BiggestChange | null = null;
  let bestWeight = 0;
  for (const row of view.rows) {
    if (row.kind !== 'number' && row.kind !== 'scale') continue;
    const cell = row.cells[index];
    if (cell?.state !== 'value' || cell.number === null || cell.delta === null || cell.delta === 0) continue;
    const series = view.series.find(
      (entry): entry is SurveyPlotView => entry.key === row.key && entry.kind !== 'binary',
    );
    if (series === undefined || series.points.length < 2) continue;
    const previous = cell.number - cell.delta;
    const scale = row.kind === 'scale' ? SCALE_STEPS : Math.abs(previous);
    if (scale === 0) continue;
    const weight = Math.abs(cell.delta) / scale;
    if (weight > bestWeight) {
      best = { row, series, delta: cell.delta, previous, current: cell.number, tone: cell.deltaTone ?? 'flat' };
      bestWeight = weight;
    }
  }
  return best;
}
